import { useQuery } from '@tanstack/react-query'
import { ApiError, type NodeInfo } from '../../api'
import { POLL_MS, RELAY, getAsOf, relayKeys, type NodeInfoAsOf, type RelayRole, type RelayLink, type RelayList, type RelayNodeEvent, type RelayRoute } from './api'
import { noteRelayAsOf, noteRelayFailure, type RelayLive } from './live'
import { nodeView, type NodeView } from './states'

/*
 * Data of Network › Nodes and the node card (04-admin-api.md §5.1, §5.3;
 * handoff/nodes.md (d)). The REST answers are the base; while the SSE stream
 * is open its node_relay / route / link events are newer and win. Without the
 * stream every list is polled every 5 s (the freshness banner says so).
 */

/**
 * The relay part of a node from the stream over the REST answer (status, relay,
 * load, clock) — unless the answer is newer (restAsOf, e.g. the answer of a PATCH).
 */
export function withLive(n: NodeInfo, ev: RelayNodeEvent | undefined, restAsOf?: string): NodeInfo {
  if (!ev) return n
  const evAsOf = (ev as RelayNodeEvent & { as_of?: string }).as_of
  if (restAsOf && evAsOf && Date.parse(restAsOf) > Date.parse(evAsOf)) return n
  return {
    ...n,
    status: ev.status,
    relay: ev.relay ?? n.relay,
    load: ev.load ?? (ev.status === 'online' ? n.load : undefined),
    clock: ev.clock ?? (ev.status === 'online' ? n.clock : undefined),
  }
}

/**
 * Live relay legs of a node: NodeRelay.legs / legs_up when the center sends them
 * (from relay reports and the controller, independent of host/1), else
 * NodeLoad.legs / legs_up (only for a node with host/1). `known` is false when
 * neither is there.
 */
export function nodeLegs(n: Pick<NodeInfo, 'relay' | 'load'>): { legs: number; legsUp: number; known: boolean; source: 'relay' | 'load' | '' } {
  const r = n.relay
  if (r && r.legs !== undefined) return { legs: r.legs, legsUp: r.legs_up ?? 0, known: true, source: 'relay' }
  if (n.load) return { legs: n.load.legs, legsUp: n.load.legs_up, known: true, source: 'load' }
  return { legs: 0, legsUp: 0, known: false, source: '' }
}

/** GET /api/admin/nodes/{id} (NodeInfo + as_of), polled while the stream is not open. */
export function useNode(id: string, live: RelayLive) {
  return useQuery({
    queryKey: relayKeys.node(id),
    queryFn: async () => {
      try {
        const r = await getAsOf<NodeInfoAsOf>(`/api/admin/nodes/${encodeURIComponent(id)}`)
        if (live.available) noteRelayAsOf(r.asOf, r.at)
        return r
      } catch (e) {
        if (live.available) noteRelayFailure(e)
        throw e
      }
    },
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 1,
    refetchInterval: live.sse === 'open' ? 15_000 : POLL_MS,
  })
}

/** Routes whose path has the node: the stream's live routes when it is open, else GET /relay/routes?node= (polled). */
export function useNodeRoutes(id: string, live: RelayLive) {
  const fromLive = live.sse === 'open' && live.loaded
  const q = useQuery({
    queryKey: relayKeys.nodeRoutes(id),
    enabled: live.available === true && !fromLive,
    queryFn: async () => {
      try {
        const r = await getAsOf<RelayList<RelayRoute>>(`${RELAY}/routes?node=${encodeURIComponent(id)}&limit=500`)
        noteRelayAsOf(r.asOf, r.at)
        return r
      } catch (e) {
        noteRelayFailure(e)
        throw e
      }
    },
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 1,
    refetchInterval: POLL_MS,
  })
  const items = fromLive ? [...live.routes.values()].filter((r) => r.path.some((p) => p.id === id)) : (q.data?.data.items ?? []).filter((r) => r.state !== 'ended')
  return { items, loading: !fromLive && q.isPending, error: fromLive || q.data ? null : q.error, refetch: q.refetch, fetching: q.isFetching }
}

/** Links with the node at one end: the stream's links when it is open, else GET /relay/links?node= (polled). */
export function useNodeLinks(id: string, live: RelayLive) {
  const fromLive = live.sse === 'open' && live.loaded
  const q = useQuery({
    queryKey: relayKeys.nodeLinks(id),
    enabled: live.available === true && !fromLive,
    queryFn: async () => {
      try {
        const r = await getAsOf<RelayList<RelayLink>>(`${RELAY}/links?node=${encodeURIComponent(id)}&limit=500`)
        noteRelayAsOf(r.asOf, r.at)
        return r
      } catch (e) {
        noteRelayFailure(e)
        throw e
      }
    },
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 1,
    refetchInterval: POLL_MS,
  })
  const all = fromLive ? [...live.links.values()].filter((l) => l.from.id === id || l.to.id === id) : (q.data?.data.items ?? [])
  const items = [...all].sort((a, b) => LINK_ORDER.indexOf(a.state) - LINK_ORDER.indexOf(b.state) || a.link_id.localeCompare(b.link_id))
  return { items, loading: !fromLive && q.isPending, error: fromLive || q.data ? null : q.error, refetch: q.refetch, fetching: q.isFetching }
}

const LINK_ORDER: RelayLink['state'][] = ['down', 'degraded', 'unknown', 'up', 'idle']

/* ── the list: filters and sorting ───────────────────────────────────────── */

export type NodeSort = 'state' | 'name' | 'region' | 'cpu' | 'streams' | 'tx'
export const SORTS: { value: NodeSort; label: string }[] = [
  { value: 'state', label: 'Problems first' },
  { value: 'name', label: 'Name' },
  { value: 'region', label: 'Region' },
  { value: 'cpu', label: 'CPU' },
  { value: 'streams', label: 'Streams' },
  { value: 'tx', label: 'Traffic out' },
]

/** Problems first; the rest by name. */
export const VIEW_ORDER: NodeView[] = ['offline', 'degraded', 'not_in_peers', 'online', 'revoked']
export const PROBLEMS: NodeView[] = ['offline', 'degraded', 'not_in_peers']

export type StateFilter = '' | 'problems' | NodeView

export interface NodeFilter {
  q: string
  state: StateFilter
  region: string
  role: '' | RelayRole
  sort: NodeSort
  revoked: boolean
}

const streamsOf = (n: NodeInfo) => (n.load ? n.load.streams_ingress + n.load.streams_transit + n.load.streams_egress : n.streams)

export function filterNodes(nodes: NodeInfo[], f: NodeFilter): NodeInfo[] {
  const q = f.q.trim().toLowerCase()
  const xs = nodes.filter((n) => {
    const v = nodeView(n)
    if (v === 'revoked' && !f.revoked && f.state !== 'revoked') return false
    if (f.state === 'problems' && !PROBLEMS.includes(v)) return false
    if (f.state && f.state !== 'problems' && v !== f.state) return false
    if (f.region && n.region !== f.region) return false
    if (f.role && !(n.roles ?? []).includes(f.role)) return false
    if (q && !`${n.name} ${n.id} ${n.region} ${n.provider ?? ''} ${n.relay?.spki ?? ''} ${n.version ?? ''}`.toLowerCase().includes(q)) return false
    return true
  })
  const by: Record<NodeSort, (a: NodeInfo, b: NodeInfo) => number> = {
    state: (a, b) => VIEW_ORDER.indexOf(nodeView(a)) - VIEW_ORDER.indexOf(nodeView(b)),
    name: () => 0,
    region: (a, b) => a.region.localeCompare(b.region),
    cpu: (a, b) => (b.load?.cpu_pct ?? -1) - (a.load?.cpu_pct ?? -1),
    streams: (a, b) => streamsOf(b) - streamsOf(a),
    tx: (a, b) => (b.load?.tx_bps ?? b.egress_bps ?? -1) - (a.load?.tx_bps ?? a.egress_bps ?? -1),
  }
  return xs.sort((a, b) => by[f.sort](a, b) || a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
}
